import { GitPushError } from '../all-errors';
import { GitPushClientError } from '../errors/git-push-client.git-error';
import { GitPushServerError } from '../errors/git-push-server.git-error';
import { createErrorResponse } from '../util/git-push-error/create-error-response';
import { build400ErrorMessage } from '../util/git-push-error/message-builders/dbt/400-error-builder';
import { GitPush400ErrorPayload } from '../util/git-push-error/models/dbt.model';
import { sanitizePayload } from '../util/git-push-error/sanitize-payload';
import { setToken } from '../util/set-token';
import { LibGit2Error } from '../wasm-git-worker-errors.model';
import { WorkerAction, WorkerFnParams } from '../wasm-git.worker.model';
import type { GitAction } from './actions.enum';

export interface GitPushAction extends WorkerAction {
  type: GitAction.Push;
  params: { refSpec?: string; authToken: string };
}

export async function GitPushActionFn({ lg, util, params }: WorkerFnParams<GitPushAction>): Promise<void> {
  try {
    setToken(params.authToken);
    lg.callAndCaptureOutput(['push', ...(params.refSpec ? [params.refSpec] : [])]);
    await util.syncfs(false);
  } catch (err) {
    const rawBytes = (err as LibGit2Error).rawBytes;

    if (!rawBytes) {
      throw err;
    }

    const sanitizedPayload = await sanitizePayload(rawBytes);
    if (!sanitizedPayload) {
      throw new GitPushError('Failed to sanitize git push error response payload', false);
    }

    const errorResponse = await createErrorResponse(sanitizedPayload);
    if (!errorResponse) {
      throw new GitPushError('Failed to parse git push error response payload', false);
    }

    if (errorResponse.code === 400) {
      throw new GitPushClientError(
        build400ErrorMessage(errorResponse.body as GitPush400ErrorPayload),
        false,
        errorResponse.body as GitPush400ErrorPayload,
      );
    }

    if (errorResponse.code === 500) {
      throw new GitPushServerError();
    }

    throw new GitPushError((errorResponse.body as GitPushError).message, false, errorResponse.body);
  }
}
